import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfDay,
  format,
  isAfter,
  isBefore,
  isEqual,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { sortBy } from 'lodash';
import { HitterProfilePeriod } from '../enums/hitters.enums';
import { ITotalBpChartUnit } from '../enums/session-hits.enums';
import {
  ISessionHit,
  ITotalBpChartBucket,
} from '../interfaces/session-hits/i-session-hit';

/**
 * Helpers for charting # of BP faced over time
 * A "bucket" is a time period that we group hits into. (e.g. day, month, etc)
 *
 * 1 - Determine how many buckets we need based on the period and unit, and how long each bucket is
 * 2 - Create empty buckets for the entire period
 * 3 - Iterate through hits and increment the appropriate bucket based on the hit date
 */

// returns the starting date for a bucket based on the unit (used as a key for grouping hits)
const getTotalBpBucketKey = (date: Date, unit: ITotalBpChartUnit): Date => {
  if (unit === ITotalBpChartUnit.Day) {
    return startOfDay(date);
  }

  return startOfMonth(date);
};

// increments the date based on the provided unit
const incrementDate = (
  date: Date,
  unit: ITotalBpChartUnit,
  increment = 1
): Date => {
  if (unit === ITotalBpChartUnit.Day) {
    return addDays(date, increment);
  }

  return addMonths(date, increment);
};

// iterates from start to end, creating an empty bucket for each step
const createTotalBpBuckets = (
  start: Date,
  end: Date,
  unit: ITotalBpChartUnit
): ITotalBpChartBucket[] => {
  const buckets: ITotalBpChartBucket[] = [];

  let bucketStart = getTotalBpBucketKey(start, unit);

  while (!isAfter(bucketStart, end)) {
    const key = bucketStart.getTime();

    buckets.push({ date: key, count: 0 });
    bucketStart = incrementDate(bucketStart, unit);
  }

  return buckets;
};

// returns chart unit and label format based on the selected period
const getTotalBpChartUnit = (
  period: HitterProfilePeriod
): ITotalBpChartUnit => {
  switch (period) {
    case HitterProfilePeriod.Last7Days:
    case HitterProfilePeriod.Last1Month:
      return ITotalBpChartUnit.Day;

    case HitterProfilePeriod.Last3Months:
    case HitterProfilePeriod.Last6Months:
      return ITotalBpChartUnit.Month;

    case HitterProfilePeriod.YTD: {
      const now = new Date();
      const jan1 = startOfYear(now);

      const daysSince = differenceInCalendarDays(now, jan1) + 1;

      // if less than 7 days, one tick per day
      if (daysSince <= 7) {
        return ITotalBpChartUnit.Day;
      }

      // otherwise one tick every month
      return ITotalBpChartUnit.Month;
    }

    case HitterProfilePeriod.Last1Year:
    default:
      return ITotalBpChartUnit.Month;
  }
};

const getTotalBpChartTicks = (config: {
  start: Date;
  end: Date;
  period: HitterProfilePeriod;
  buckets: ITotalBpChartBucket[];
}): {
  ticks: number[];
  tickFormatter: (tick: number) => string;
} => {
  const { start, end, period, buckets } = config;

  const startDate = startOfDay(start);
  const endDate = endOfDay(end);

  // so the chart knows whether to show month + may or just month for tick labels
  const dayFormatter = (tick: number) => format(new Date(tick), 'MMM d');
  const monthFormatter = (tick: number) => format(new Date(tick), 'MMM');

  // * special case handling - for Last1Month we bucket hits by day but we'll limit the number of ticks to ~1 every 5 days for readability
  if (period === HitterProfilePeriod.Last1Month) {
    const totalDays = differenceInCalendarDays(endDate, startDate) + 1; // inclusive of both start and end days
    const tickIncrement = Math.round(totalDays / 6); // ~5 days

    const ticks: number[] = [];

    let currentDate = startDate;

    while (isBefore(currentDate, endDate) || isEqual(currentDate, endDate)) {
      ticks.push(currentDate.getTime());
      currentDate = incrementDate(
        currentDate,
        ITotalBpChartUnit.Day,
        tickIncrement
      );
    }

    // for sanity - always include the end date as the last tick
    const endTick = startOfDay(endDate).getTime();
    if (ticks[ticks.length - 1] !== endTick) {
      ticks.push(endTick);
    }

    return {
      ticks,
      tickFormatter: dayFormatter,
    };
  }

  // otherwise just provide 1 tick per bucket (day or month)
  const unit = getTotalBpChartUnit(period);
  const ticks = buckets.map(({ date }) => new Date(date).getTime());

  if (unit === ITotalBpChartUnit.Day) {
    return {
      ticks,
      tickFormatter: dayFormatter,
    };
  }

  return {
    ticks,
    tickFormatter: monthFormatter,
  };
};

export class HitterProfileHelper {
  private static getPeriodStartDate(
    period: HitterProfilePeriod,
    endDate: Date
  ) {
    switch (period) {
      case HitterProfilePeriod.Last7Days: {
        return startOfDay(subDays(endDate, 6));
      }

      case HitterProfilePeriod.Last1Month: {
        return startOfDay(subMonths(endDate, 1));
      }

      case HitterProfilePeriod.Last3Months: {
        return startOfDay(subMonths(endDate, 3));
      }

      case HitterProfilePeriod.Last6Months: {
        return startOfDay(subMonths(endDate, 6));
      }

      case HitterProfilePeriod.YTD: {
        return startOfYear(endDate);
      }

      case HitterProfilePeriod.Last1Year: {
        return startOfDay(subYears(endDate, 1));
      }

      default: {
        return endDate;
      }
    }
  }

  // returns the starting date for the current period
  static getCurrentStartDate(period: HitterProfilePeriod, endDate?: Date) {
    return this.getPeriodStartDate(period, endOfDay(endDate ?? new Date()));
  }

  // returns the starting date for the previous period
  static getPreviousStartDate(period: HitterProfilePeriod, endDate?: Date) {
    // i.e. start counting from 1 period ago
    const currentStart = this.getCurrentStartDate(period, endDate);

    switch (period) {
      case HitterProfilePeriod.YTD: {
        // * this is different from everything else because of YTD
        return subYears(currentStart, 1);
      }

      default: {
        // i.e. go 1 more period back
        return this.getPeriodStartDate(period, currentStart);
      }
    }
  }

  static getPreviousEndDate(period: HitterProfilePeriod) {
    // i.e. start counting from 1 period ago
    switch (period) {
      case HitterProfilePeriod.YTD: {
        return subYears(new Date(), 1);
      }

      default: {
        return this.getCurrentStartDate(period);
      }
    }
  }

  static getTotalBpChartData(config: {
    hits: ISessionHit[];
    period: HitterProfilePeriod;
  }): {
    data: ITotalBpChartBucket[];
    ticks: number[];
    tickFormatter: (tick: number) => string;
  } {
    // are we grouping (bucketing) hits by day or month?
    const chartUnit = getTotalBpChartUnit(config.period);

    const chartEndDate = endOfDay(new Date());
    const chartStartDate = HitterProfileHelper.getCurrentStartDate(
      config.period,
      chartEndDate
    );

    // create an empty bucket for each day/month in the period (key is the starting date of the bucket)
    const buckets = createTotalBpBuckets(
      chartStartDate,
      chartEndDate,
      chartUnit
    );

    // only include hits in the current period
    const currentHits = config.hits.filter((h) =>
      isAfter(parseISO(h._created), chartStartDate)
    );

    for (const h of currentHits) {
      const created = new Date(h._created);

      // get the starting DT of the bucket this hit belongs to
      // e.g. if bucketing by month, all hits in June 2023 will return June 1, 2023
      // we use this as the key to find the bucket we need to increment
      const bucketKey = getTotalBpBucketKey(created, chartUnit).getTime();
      const bucket = buckets.find((b) => b.date === bucketKey);

      if (!bucket) {
        console.warn('No bucket found for hit', h, bucketKey);
        continue;
      }

      bucket.count += 1;
    }

    // sort by date ascending
    const data = sortBy(buckets, (b) => b.date);

    // we show 1 tick per bucket for all periods except Last1Month which buckets hits by day, but we display ~1 tick every 5 days for readability
    const { ticks, tickFormatter } = getTotalBpChartTicks({
      start: chartStartDate,
      end: chartEndDate,
      period: config.period,
      buckets: data,
    });

    return {
      data,
      ticks,
      tickFormatter,
    };
  }
}
